import { ButtonBase, Stack } from '@mui/material';
import { useMessageContentTabIndex } from '@mui/x-chat/headless';
import { CaretRightIcon } from '@phosphor-icons/react';
import { type PropsWithChildren, useId, useState } from 'react';

import { Text } from 'common';

import { AgenticChatActivityIcon } from './Icon';

interface AgenticChatActivityDisclosureProps extends PropsWithChildren {
  label: string;
  animation: 'Active' | 'Inactive';
  errorCount?: number;
  defaultExpansion?: 'Expanded' | 'Collapsed';
  icon?: 'Visible' | 'Hidden';
  focus?: 'Restore' | 'Preserve';
  onFocusChange?: (focus: 'Focused' | 'Blurred') => void;
}

export function AgenticChatActivityDisclosure(props: AgenticChatActivityDisclosureProps) {
  const {
    label,
    animation,
    errorCount = 0,
    children,
    defaultExpansion = 'Collapsed',
    icon = 'Visible',
    focus = 'Preserve',
    onFocusChange,
  } = props;
  const [selection, setSelection] = useState<{
    defaultExpansion: 'Expanded' | 'Collapsed';
    expansion: 'Expanded' | 'Collapsed';
  }>();
  const isOpen =
    selection?.defaultExpansion === defaultExpansion
      ? selection.expansion === 'Expanded'
      : defaultExpansion === 'Expanded';
  const tabIndex = useMessageContentTabIndex();
  const [contentFocus, setContentFocus] = useState<'Focused' | 'Blurred'>('Blurred');
  const contentId = useId();
  return (
    <Stack
      onFocusCapture={() => onFocusChange?.('Focused')}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          onFocusChange?.('Blurred');
        }
      }}
      sx={{ typography: 'body2' }}
    >
      <ButtonBase
        ref={
          focus === 'Restore' || (!isOpen && contentFocus === 'Focused')
            ? (element: HTMLButtonElement | null) => {
                // The trigger stays mounted, so a callback ref handles prop-driven focus changes.
                element?.focus();
              }
            : undefined
        }
        type="button"
        disableRipple
        aria-expanded={isOpen}
        aria-controls={contentId}
        tabIndex={tabIndex}
        onClick={() => {
          setSelection({ defaultExpansion, expansion: isOpen ? 'Collapsed' : 'Expanded' });
        }}
        sx={{
          font: 'inherit',
          justifyContent: 'flex-start',
          textAlign: 'left',
          minHeight: (theme) => theme.spacing(3),
          gap: 0.5,
          color: isOpen ? 'text.primary' : 'text.secondary',
          fontWeight: isOpen ? 'fontWeightMedium' : 'fontWeightRegular',
          '&:hover, &:focus-visible': {
            color: 'text.primary',
          },
          '&:focus-visible': { outline: 'revert' },
        }}
      >
        {icon === 'Visible' && <AgenticChatActivityIcon animation={animation} />}
        <Text
          component="span"
          variant="body2"
          color="inherit"
          sx={{ fontWeight: 'inherit', minWidth: 0, overflowWrap: 'anywhere' }}
        >
          {label}
        </Text>
        {errorCount > 0 && (
          <Text component="span" variant="body2" color="error">
            {`${errorCount} ${errorCount === 1 ? 'error' : 'errors'}`}
          </Text>
        )}
        <CaretRightIcon
          size={12}
          aria-hidden
          style={{ flexShrink: 0, transform: isOpen ? 'rotate(90deg)' : 'none' }}
        />
      </ButtonBase>
      <Stack
        id={contentId}
        hidden={!isOpen}
        onFocusCapture={() => setContentFocus('Focused')}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setContentFocus('Blurred');
          }
        }}
        sx={{
          gap: 1,
          paddingTop: 1,
          color: 'text.secondary',
          '&[hidden]': { display: 'none' },
        }}
      >
        {children}
      </Stack>
    </Stack>
  );
}
